import { PractitionerSearchResultApi } from 'src/app/utils/interfaces/new/PractitionerSearchResultApi';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LogFonctionnelService, ActionEnum, ResultEnum, TypeTargetEnum } from '../log-fonctionnel/log-fonctionnel.service';
import moment from 'moment';
import { IdentifiantsTemporairesConnexion } from 'src/app/utils/interfaces/new/identifiantsTemporairesConnexion';
import { MSSClinicalDocumentInfo } from 'src/app/utils/interfaces/new/mss-clinical-document-info.interface';
import { EmailAuthent } from 'src/app/utils/interfaces/new/mss-email-authent.interface';
import { MSSFolderValidate } from 'src/app/utils/interfaces/new/mss-folder-validate.interface';
import { MSSFolder } from 'src/app/utils/interfaces/new/mss-folder.interface';
import { MSSMessageValidate } from 'src/app/utils/interfaces/new/mss-message-validate.interface';
import { MSSAttachment, MSSMessage, MSSMessageToSend } from 'src/app/utils/interfaces/new/mss-message.interface';
import { MSSFullTextSearchCriteria, MSSSearchCriteria } from 'src/app/utils/interfaces/new/mss-search-criteria.interface';
import { MSSSyncMessagesResponse } from 'src/app/utils/interfaces/new/mss-sync-messages-response.interface';
import { PractitionerSearchResult } from 'src/app/utils/interfaces/new/PractitionerSearchResult';
import { RessourceSetting } from 'src/app/utils/interfaces/new/RessourceSetting';
import { environment } from 'src/environments/environment';
import { AnnuaireProfessionnelsSanteService } from '../annuaire-professionnels-sante/annuaire-professionnels-sante.service';
import { FiltresService } from '../filtres/filtres.service';
import { RessourceService } from '../ressource/ressource.service';
import { MssServiceAbstract, PractitionerSearchParams, PractitionerSearchParamsAPICRYPT } from './mss-service.abstract';
import { MSSNotAuthorizedError } from './mss-service.factory';

const MESSAGERIE_PATH = '/messagerie/';

const MSS_PATHS = {
  loginOTP: 'login/otp',
  validateOTP: 'login/validate',
  listEmails: 'annuaire/emails',
  listFolders: 'folders/list/',
  createFolder: 'folders/create/',
  trashFolder: 'folders/delete/',
  renameFolder: 'folders/rename/',
  searchMessages: 'messages/search/',
  fullTextSearchMessages: 'messages/searchpleintexte/',
  moveMessages: 'messages/move/',
  syncMessages: 'messages/sync/', // Récupération OK -> reste à gérer le différentiel
  downloadAttachment: 'attachments/download/',  // Test avec de gros fichiers
  removeAttachment: 'attachments/remove/',
  deleteMessage: 'messages/delete/',  // OK
  draftMessage: 'messages/draft/',  // OK
  sendMessage: 'messages/send/',  // OK
  markMessageAsRead: 'messages/markasread/',  // OK
  releverMessage: 'messages/relever',  // OK
};

const MSS_IDNAT_TOKENS = 'MSS_IDNAT_TOKENS';
interface MssIdnatTokenInterface {
  idnat: string;
  token: string;
}


@Injectable({
  providedIn: 'root'
})
export class MssMailizService extends MssServiceAbstract {
  canGetMessage(): boolean {
    return false;
  }
  getMessageById(email: EmailAuthent, messageId: string): Promise<MSSMessage> {
    throw new Error('Method not implemented (method used by ApiCrypt service only).');
  }

  private basePath = environment.basePath + MESSAGERIE_PATH;

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  constructor(
    protected _http: HttpClient,
    private _apsService: AnnuaireProfessionnelsSanteService,
    private _resourceSrv: RessourceService,
    protected _filtreSrv: FiltresService,
    protected _logSrv: LogFonctionnelService,
  ) {
    super(_http, _filtreSrv, _logSrv);
  }

  listFolders(email: EmailAuthent, folderId?: number): Promise<MSSFolder[]> {
    this.setCredentials(email.idNat);
    return new Promise<MSSFolder[]>(async (resolve, reject) => {
      try {
        let url = `${this.basePath}${MSS_PATHS.listFolders}${email.email}`;
        if (folderId) {
          url += `?parentFolderId=${folderId}`;
        }
        const folders = await this._http.get<MSSFolder[]>(
          url,
          this.httpOptions
        ).toPromise();
        resolve(folders.body);
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  createFolder(email: EmailAuthent, folderName: string, folderParentId: number): Promise<MSSFolderValidate> {
    this.setCredentials(email.idNat);
    return new Promise<MSSFolderValidate>(async (resolve, reject) => {
      try {
        const folders = await this._http.post<MSSFolderValidate>(
          `${this.basePath}${MSS_PATHS.createFolder}${email.email}/${folderName}/${folderParentId}`,
          {},
          this.httpOptions
        ).toPromise();
        resolve(folders.body);
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  trashFolder(email: EmailAuthent, folderId: number): Promise<void> {
    this.setCredentials(email.idNat);
    return new Promise<void>(async (resolve, reject) => {
      try {
        const folders = await this._http.delete<void>(
          `${this.basePath}${MSS_PATHS.trashFolder}${email.email}/${folderId}`,
          this.httpOptions
        ).toPromise();
        resolve();
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  renameFolder(email: EmailAuthent, folderId: number, newFolderName: string): Promise<void> {
    this.setCredentials(email.idNat);
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.put<void>(
          `${this.basePath}${MSS_PATHS.renameFolder}${email.email}/${folderId}/${newFolderName}`,
          {},
          this.httpOptions
        ).toPromise();
        resolve();
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  searchMessages(email: EmailAuthent, searchCriteria: MSSSearchCriteria): Promise<MSSMessage[]> {
    this.setCredentials(email.idNat);
    return new Promise<MSSMessage[]>(async (resolve, reject) => {
      try {
        const folders = await this._http.post<MSSMessage[]>(
          `${this.basePath}${MSS_PATHS.searchMessages}${email.email}`,
          {...searchCriteria},
          this.httpOptions
        ).toPromise();
        if (folders.body && folders.body.length > 0) {
          const rtd: Array<MSSMessage> = [];
          for (const msg of folders.body) {
            msg.date = moment(msg.date, 'DD/MM/YYYY HH:mm:ss').toDate();
            rtd.push(msg);
          }
          resolve(rtd);
        } else {
          resolve([]);
        }
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Recherche/Relève messages MSS', details: `POST ${MSS_PATHS.searchMessages}` });
      } catch (e) {
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Recherche/Relève messages MSS', details: `POST ${MSS_PATHS.searchMessages} - Erreur: ${e?.error?.message || 'Erreur inconnue'}` });
        reject(e?.error);
      }
    });
  }

  moveMessages(email: EmailAuthent, messageIds: number[], destinationFolderId: number): Promise<void> {
    this.setCredentials(email.idNat);
    return new Promise<void>(async (resolve, reject) => {
      try {
        const folders = await this._http.put<void>(
          `${this.basePath}${MSS_PATHS.moveMessages}${email.email}/${destinationFolderId}`,
          messageIds,
          this.httpOptions
        ).toPromise();
        resolve(folders.body);
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  syncMessages(email: EmailAuthent, folderId?: number, syncToken?: string): Promise<MSSSyncMessagesResponse> {
    this.setCredentials(email.idNat);
    return new Promise<MSSSyncMessagesResponse>(async (resolve, reject) => {
      const httpOptions = {...this.httpOptions};

      if (!httpOptions.params) {
        httpOptions.params = {};
      }

      try {
        if (folderId) {
          httpOptions.params.folderId = folderId;
        }

        if (syncToken && syncToken !== '') {
          httpOptions.params.syncToken = syncToken;
        }

        const syncMessages = await this._http.post<MSSSyncMessagesResponse>(
          `${this.basePath}${MSS_PATHS.syncMessages}${email.email}`,
          undefined,
          httpOptions
        ).toPromise();
        resolve(syncMessages.body);
        const count = syncMessages.body?.modifiedMessages?.length || 0;
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Relève messages MSS', details: `POST ${MSS_PATHS.syncMessages} - ${count} message(s) relevé(s)` });
      } catch (e) {
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Relève messages MSS', details: `POST ${MSS_PATHS.syncMessages} - Erreur: ${e?.error?.message || 'Erreur inconnue'}` });
        reject(e?.error);
      }
    });
  }

  downloadAttachment(email: EmailAuthent, messageId: number, attachment: MSSAttachment): Promise<{file: File, base64: string}> {
    const base64toFile = async (base64: string, filename: string, mimeType: string): Promise<File> => {
      if (!base64.includes(`data:${mimeType};base64,`)) {
        base64 = `data:${mimeType};base64,${base64}`;
      }

      return new Promise<File>((resolve) => {
        fetch(base64).then((res) => {
          return res.arrayBuffer();
        }).then((buf) => {
          resolve(new File([buf], filename, { type: mimeType }));
        });
      });
    };

    this.setCredentials(email.idNat);
    return new Promise<{file: File, base64: string}>(async (resolve, reject) => {
      try {
        const base64 = await this._http.post<string>(
          `${this.basePath}${MSS_PATHS.downloadAttachment}${email.email}/${messageId}/${attachment.part}`,
          undefined,
          this.httpOptions
        ).toPromise();
        resolve({
          file: await base64toFile(base64.body, attachment.fileName, attachment.contentType),
          base64: base64.body
        });
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  public async removeAttachment(email: EmailAuthent, messageId: number, attachment: MSSAttachment): Promise<void> {
    this.setCredentials(email.idNat);
    return new Promise<void>(async (resolve, reject) => {
      try {
        const base64 = await this._http.delete<string>(
          `${this.basePath}${MSS_PATHS.removeAttachment}${email.email}/${messageId}/${attachment.part}`,
          this.httpOptions
        ).toPromise();
        resolve();
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  deleteMessage(email: EmailAuthent, messageId: number | number[]): Promise<void> {
    this.setCredentials(email.idNat);
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.put<void>(
          `${this.basePath}${MSS_PATHS.deleteMessage}${email.email}`,
          Array.isArray(messageId) ? messageId : [messageId],
          this.httpOptions
        ).toPromise();
        resolve();
        this._addLog({ action: ActionEnum.Delete, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Suppression messages MSS', details: `DELETE ${MSS_PATHS.deleteMessage} (IDs: ${Array.isArray(messageId) ? messageId.join(',') : messageId})` });
      } catch (e) {
        this._addLog({ action: ActionEnum.Delete, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Suppression messages MSS', details: `DELETE ${MSS_PATHS.deleteMessage} - Erreur: ${e?.error?.message || 'Erreur inconnue'}` });
        reject(e?.error);
      }
    });
  }

  draftMessage(email: EmailAuthent, message: MSSMessageToSend): Promise<MSSMessageValidate> {
    this.setCredentials(email.idNat);
    return new Promise<MSSMessageValidate>(async (resolve, reject) => {
      try {
        // On encapsule le body dans <![CDATA[ ... ]]> pour qu'il soit bien enregistré par l'API (pour contenu HTML)
        message.messageData.body = message.messageData.body && message.messageData.body !== '' ? `<![CDATA[${message.messageData.body}]]>` : '';

        if (message.messageData.attachments) {
          for (const att of message.messageData.attachments) {
            // Si la propriété part est présente c'est que la pièce-jointe existe déjà
            if (!att.part) {
              /**
               * On passe les propriétés 'messageId' et 'part' à undefined
               * afin que l'API voit la pièce-jointe comme une nouvelle pièce à ajouter
               */
              att.messageId = undefined;
              att.part = undefined;
            } else {
              /**
               * On passe les propriétés 'contentType', 'file' et 'fileName' à undefined
               * afin que l'API voit la pièce-jointe comme une pièce jointe déjà existante
               */
              att.messageId = message.messageData.messageId;
              att.contentType = undefined;
              att.file = undefined;
              att.fileName = undefined;
            }
          }
        }

        if (message.messageData.addresses) {
          for (const add of message.messageData.addresses) {
            add.name = undefined;
          }
        }

        const msg = await this._http.post<Array<MSSMessageValidate>>(
          `${this.basePath}${MSS_PATHS.draftMessage}${email.email}`,
          message,
          this.httpOptions
        ).toPromise();
        resolve(msg.body[0]);
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  sendMessage(email: EmailAuthent, message: MSSMessageToSend, clinicalDocumentInfo?: MSSClinicalDocumentInfo): Promise<MSSMessageValidate> {
    this.setCredentials(email.idNat);
    return new Promise<MSSMessageValidate>(async (resolve, reject) => {
      try {
        // On encapsule le body dans <![CDATA[ ... ]]> pour qu'il soit bien enregistré par l'API (pour contenu HTML)
        message.messageData.body = message.messageData.body && message.messageData.body !== '' ?
          `<![CDATA[${message.messageData.body}]]>` : '';

        const msg = await this._http.post<MSSMessageValidate>(
          `${this.basePath}${MSS_PATHS.sendMessage}${email.email}`,
          {
            message,
            clinicalDocumentInfo
          },
          this.httpOptions
        ).toPromise();
        resolve(msg.body);
        this._addLog({ action: ActionEnum.Create, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Envoi message MSS', details: `POST ${MSS_PATHS.sendMessage}` });
      } catch (e) {
        this._addLog({ action: ActionEnum.Create, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Envoi message MSS', details: `POST ${MSS_PATHS.sendMessage} - Erreur: ${e?.error?.message || 'Erreur inconnue'}` });
        if (!environment.production) {
          resolve({
            date: new Date(),
            messageId: 1234,
            size: 42
          });
        } else {
          reject(e?.error);
        }
      }
    });
  }

  markMessageAsRead(email: EmailAuthent, messageId: number | number[]): Promise<void> {
    this.setCredentials(email.idNat);
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.put<void>(
          `${this.basePath}${MSS_PATHS.markMessageAsRead}${email.email}`,
          Array.isArray(messageId) ? messageId : [messageId],
          this.httpOptions
        ).toPromise();
        resolve();
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  fullTextSearchMessages(email: EmailAuthent, searchCriteria: MSSFullTextSearchCriteria): Promise<MSSMessage[]> {
    this.setCredentials(email.idNat);
    return new Promise<MSSMessage[]>(async (resolve, reject) => {
      try {
        const folders = await this._http.post<MSSMessage[]>(
          `${this.basePath}${MSS_PATHS.fullTextSearchMessages}${email.email}`,
          searchCriteria,
          this.httpOptions
        ).toPromise();
        resolve(folders.body);
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  findMSSRecipient(params: PractitionerSearchParams, limit: number, offset: number,
                   doForceSearch: boolean): Promise<PractitionerSearchResult> {
    return new Promise<PractitionerSearchResult>(async (resolve, reject) => {
      try {
        resolve(await this._apsService.searchProfessionals(params, limit, offset, doForceSearch));
      } catch (e) {
        reject(e);
      }
    });
  }

  findMSSRecipientApi(params: PractitionerSearchParamsAPICRYPT, limit: number, offset: number,
                   doForceSearch: boolean): Promise<PractitionerSearchResultApi> {
    return new Promise<PractitionerSearchResultApi>(async (resolve, reject) => {
      try {
        resolve(await this._apsService.searchProfessionalsApi(params, limit, offset, doForceSearch));
      } catch (e) {
        reject(e);
      }
    });
  }

  private getSettings = (params: RessourceSetting[], settingKey: string): string|undefined => {
    const temp = params.find(p => p.settingKey === settingKey);
    return temp ? temp.settingValue : undefined;
  }

  public async connectToMSSByIDNAT(mail: string, password: string, typeOTP: string): Promise<IdentifiantsTemporairesConnexion> {
    return new Promise<IdentifiantsTemporairesConnexion>(async (resolve, reject) => {
      try {
        const identifiants = await this._http.post<IdentifiantsTemporairesConnexion>(
          `${this.basePath}${MSS_PATHS.loginOTP}`,
          {
            idNat: mail,
            password,
            typeOTP
          },
          this.httpOptions
        ).toPromise();
        resolve(identifiants.body);
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Connexion MSSanté', details: `POST ${MSS_PATHS.loginOTP}` });
      } catch (e) {
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Connexion MSSanté', details: `POST ${MSS_PATHS.loginOTP} - Erreur: \${e?.error?.message || 'Erreur inconnue'}` });
        reject(e?.error);
      }
    });
  }

  public async validateOTP(otp: string, identifiants: IdentifiantsTemporairesConnexion): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.post<IdentifiantsTemporairesConnexion>(
          `${this.basePath}${MSS_PATHS.validateOTP}`,
          {
            OTP: otp,
            JSESSIONID: identifiants.JSESSIONID,
            AMAuthCookie: identifiants.AMAuthCookie,
            amlbcookie: identifiants.amlbcookie,
            wwwauthenticate: identifiants.wwwauthenticate,
            body: identifiants.body
          },
          this.httpOptions
        ).toPromise();
        this.authenticationErrorSubject.next(true);
        resolve();
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Success, typeTarget: TypeTargetEnum.NONE, subDomain: 'Validation OTP MSSanté', details: `POST ${MSS_PATHS.validateOTP}` });
      } catch (e) {
        this._addLog({ action: ActionEnum.Read, result: ResultEnum.Error, typeTarget: TypeTargetEnum.NONE, subDomain: 'Validation OTP MSSanté', details: `POST ${MSS_PATHS.validateOTP} - Erreur: \${e?.error?.message || 'Erreur inconnue'}` });
        reject(e?.error);
      }
    });
  }

  public storeToken(token: string, idNat: string): void {
    // On récupère tout les token pour ensuite pouvoir cibler le bon idNat
    let ssTokensStr = sessionStorage.getItem(MSS_IDNAT_TOKENS);
    if (!ssTokensStr || ssTokensStr === '') {
      ssTokensStr = '[]';
      sessionStorage.setItem(MSS_IDNAT_TOKENS, ssTokensStr);
    }
    if (ssTokensStr && ssTokensStr !== '') {
      const ssTokens = JSON.parse(ssTokensStr) as Array<MssIdnatTokenInterface>;
      let idnatExists = false;

      /**
       * On boucle sur les tokens déjà présent dans le sessionStorage
       */
      for (const t of ssTokens) {
        if (t.idnat === idNat) {
          /**
           * Si un idNat correspond à celui passé en paramètre
           * alors on modifie le token avec le nouveau (passé en paramètre)
           * et on break pour stopper la boucle
           */
          t.token = token;
          idnatExists = true;
          break;
        }
      }

      /**
       * Si on passe dans cette condition
       * c'est que l'idNat n'existe pas dans le sessionStorage
       * Dans ce cas on l'ajoute au tokens existants
       */
      if (!idnatExists) {
        ssTokens.push({ idnat: idNat, token });
      }


      // On enregistre les tokens dans le sessionStorage
      sessionStorage.setItem(MSS_IDNAT_TOKENS, JSON.stringify(ssTokens));
    }
  }

  public loadToken(idNat: string): {token: string, idNat: string} | undefined {
    // On récupère tout les token pour ensuite pouvoir cibler le bon idNat
    const ssTokensStr = sessionStorage.getItem(MSS_IDNAT_TOKENS);
    let rtd: {token: string, idNat: string} | undefined;
    if (ssTokensStr && ssTokensStr !== '') {
      const ssTokens = JSON.parse(ssTokensStr) as Array<MssIdnatTokenInterface>;

      /**
       * On boucle sur les tokens présent dans le sessionStorage
       */
      for (const t of ssTokens) {
        if (t.idnat === idNat) {
          /**
           * Si un idNat correspond à celui passé en paramètre
           * alors on récupère le couple idNat / token
           * et on break pour stopper la boucle
           */
          rtd = { idNat: t.idnat, token: t.token };
          break;
        }
      }
      return rtd;
    } else {
      return rtd;
    }
  }
  canCreateFolders(): boolean {
    return true;
  }

  canRenameFolders(): boolean {
    return true;
  }

  canRemoveFolders(): boolean {
    return true;
  }

  canListFolders(): boolean {
    return true;
  }

  canMoveMessages(): boolean {
    return true;
  }
  canSyncMessages(): boolean {
    return true;
  }

  canRemoveAttachment(): boolean {
    return true;
  }

  canDraftMessage(): boolean {
    return true;
  }

  canMarkMessageAsRead(): boolean {
    return true;
  }

  isTokenNeeded(): boolean {
    return true;
  }

  isOTPNeeded(): boolean {
    return true;
  }

  isSearchMessageHandlePagination(): boolean {
    return true;
  }
  mustDraftToAddAttachment(): boolean {
    return true;
  }
  providerLibellePrefix(): string {
    return 'Mailiz';
  }

  isSessionValid(email: EmailAuthent): Promise<boolean> {
    return new Promise<boolean>(async (resolve) => {
      try {
        await this.listFolders({ email: email.email, idNat: email.idNat });
        resolve(true);
      } catch (e) {
        resolve(false);
      }
    });
  }

  public listAccounts(idNat: string): Promise<string[]> {
    this.setCredentials(idNat);
    return new Promise<string[]>(async (resolve, reject) => {
      try {
        const emails = await this._http.get<string[]>(
          `${this.basePath}${MSS_PATHS.listEmails}`,
          this.httpOptions
        ).toPromise();
        resolve(emails.body);
      } catch (e) {
        reject(e?.error);
      }
    });
  }

  private setCredentials(idNat: string): void {
    const credentials = this.loadToken(idNat);
    if (!credentials || !credentials.idNat || !credentials.token) {
      throw new MSSNotAuthorizedError();
    }
    this.httpOptions.headers = this.httpOptions.headers.set('jsessionid', credentials.token);
    this.httpOptions.headers = this.httpOptions.headers.set('idnat', credentials.idNat);
  }

  clearToken(idNat: string): void {
    // On récupère tout les token pour ensuite pouvoir cibler le bon idNat
    const ssTokensStr = sessionStorage.getItem(MSS_IDNAT_TOKENS);
    if (ssTokensStr && ssTokensStr !== '') {
      const ssTokens = JSON.parse(ssTokensStr) as Array<MssIdnatTokenInterface>;
      const newArray = ssTokens.filter(t => t.idnat !== idNat);
      sessionStorage.setItem(MSS_IDNAT_TOKENS, JSON.stringify(newArray));
    }
  }
}
